import noPhoto from 'assets/images/nophoto-searchtile.jpg';
import mapCardNoPhoto from 'components/base/Image/nophoto.png'; // map card only, see getMapCardPhoto

// One photo rule for every surface that renders a comparable property — the
// results card's thumbnail (Results/OwnedProperties) and the map card's photo
// band (InvestorInfoPopover) — so the two can't drift apart.
//
// The service sends PropStream's own no-photo graphics in `imageUrl` rather
// than omitting the field, so a truthy URL is not a photo. These are the same
// markers the property search screens for (Search/Results, Search/Property),
// and the same tile it substitutes, so a property with no photo looks the same
// wherever it is shown.
//
// The asset is imported, never written as a path string: webpack's file-loader
// emits it to `images/[name].[ext]`, so a literal '../../assets/...' in an
// <img src> resolves against the page URL and 404s (which is what the two
// property-search call sites above currently do).
const FALLBACK_MARKERS = [
  'coming_soon',
  'photo_coming_soon',
  'house-img-not-available',
  'placeholder',
];

// Takes anything carrying `imageUrl` — a raw comparable property or a view
// model built from one.
//
// A truthy `imageUrl` is not proof of a photo: the service returns its own
// no-photo graphics (see FALLBACK_MARKERS) rather than omitting the field, so
// those are treated as "no photo" and swapped for the local tile — the same
// substitution the property-search screens make, so a photo-less property looks
// the same on every surface (this popover and Results/OwnedProperties).
export const getPropertyPhoto = (property) => {
  const url = (property && property.imageUrl) || '';

  return !url ? noPhoto : url;
};

// --- Map card photo band (InvestorInfoPopover) — ATAI-352 ---------------------
// The map card's own rule; Results/OwnedProperties keeps getPropertyPhoto above.
//
// Placeholder URLs (FALLBACK_MARKERS) and empty ones show the image the property
// search map card shows (app/components/Carousel). It is that PNG, not the tile
// above, on purpose: two webpack rules match .jpg (conf/client/webpack.base.config.js),
// and the second file-loader pass overwrites an imported .jpg with its own JS
// module text, so the tile never renders in an <img>.
//
// Real photos get the property search's host rewrite: the service can send an
// environment host (images.<env>.propstream.com), rewritten to
// images.propstream.com the way Search/Results does.

const isFallbackPhoto = (url) => !url || FALLBACK_MARKERS.some((marker) => url.includes(marker));

const toImagesHost = (url) => {
  try {
    const parsed = new URL(url);
    const hostParts = parsed.hostname.split('.');
    if (
      hostParts.length === 4 &&
      hostParts[0] === 'images' &&
      hostParts[2] === 'propstream' &&
      hostParts[3] === 'com'
    ) {
      hostParts.splice(1, 1);
      parsed.hostname = hostParts.join('.');
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

export const getMapCardPhoto = (property) => {
  const url = (property && property.imageUrl) || '';

  return isFallbackPhoto(url) ? mapCardNoPhoto : toImagesHost(url);
};

// <img onError>: a photo URL that fails to load (404, expired link) shows the
// same no-photo image instead of an empty band. Only swaps once, so a broken
// image can't loop.
export const showMapCardNoPhotoOnError = (event) => {
  const img = event.currentTarget;
  if (!img.src.endsWith(mapCardNoPhoto)) img.src = mapCardNoPhoto;
};

export default getPropertyPhoto;
